import { useQuery } from '@tanstack/react-query';
import { projectsApi } from '@/lib/apiClient';
import { projectStage } from '@/lib/projectStage';

export interface ShippingProjectOption { id: number; jobNumber: string; title: string }

/**
 * The jobs a request can be raised against or filtered by: the ones the signed-in person can see.
 *
 * ⚠️ The visibility-scoped list (`scope: 'all'` is "everything this user is permitted to see"), NOT Receiving's
 * unscoped `project-options`. The shipping API refuses a job the caller cannot see, so offering one would only lead
 * to a 404 that reads "not found".
 *
 * ⚠️ Jobs still on a SYSTAC- or ClosedWon- placeholder number are left out. The list includes them, and the route's stage
 * guard refuses a request on one (409, "no Sage job number yet"), so offering one would let a person fill in the whole
 * form and only then be told it can't be submitted. `projectStage` is the one client rule for that, the same one the job
 * workspace uses to lock its tabs.
 *
 * It is the whole Projects list (with each job's checklist dots and phases), so it is fetched only where it is needed: the
 * drawer's job picker, when a new request is being raised without a pinned job. The page's search already finds a job by number
 * or name, so the page does not load it just to open.
 */
export function useShippingProjects(enabled = true) {
  return useQuery({
    queryKey: ['shipping-projects'],
    queryFn: async (): Promise<ShippingProjectOption[]> =>
      ((await projectsApi.list({ scope: 'all' })) as ShippingProjectOption[])
        .filter((p) => !projectStage(p.jobNumber).activityLocked)
        .map((p) => ({ id: p.id, jobNumber: p.jobNumber, title: p.title })),
    staleTime: 10 * 60 * 1000,
    enabled,
  });
}
